import {IdName} from '@domain/id-name';
import {StandardSpeed} from '@domain/machine/standard-speed.enum';
import {ProductionScheduleState} from '@domain/production-schedule/production-schedule-state';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {convertSpeedAndCycleTime} from '@vdw/angular-component-library';
import moment from 'moment';
import {IdNameUnitOfMeasurement} from './id-name-unit-of-measurement';

export class PlasticProductionOrder {
  private _id: number;
  private _name: string;
  private _status: ProductionScheduleStatus;
  private _state: ProductionScheduleState;
  private _dueDate: Date;
  private _plasticProduct: IdNameUnitOfMeasurement;
  private _plannedQuantity: number;
  private _machine: IdName;
  private _machineGroup: IdName;
  private _tool: IdName;
  private _customer: IdName;
  private _plannedSpeed: number;
  private _plannedEfficiency: number;
  private _releaseForProduction: boolean;
  private _dateCreated: Date;

  public constructor(
    id: number,
    name: string,
    status: ProductionScheduleStatus,
    state: ProductionScheduleState,
    dueDate: Date,
    plasticProduct: IdNameUnitOfMeasurement,
    plannedQuantity: number,
    machine: IdName,
    machineGroup: IdName,
    tool: IdName,
    customer: IdName,
    plannedSpeed: number,
    plannedEfficiency: number,
    releaseForProduction: boolean,
    dateCreated?: Date
  ) {
    this._id = id;
    this._name = name;
    this._status = status;
    this._state = state;
    this._dueDate = dueDate;
    this._plasticProduct = plasticProduct;
    this._plannedQuantity = plannedQuantity;
    this._machine = machine;
    this._machineGroup = machineGroup;
    this._tool = tool;
    this._customer = customer;
    this._plannedSpeed = plannedSpeed;
    this._plannedEfficiency = plannedEfficiency;
    this._releaseForProduction = releaseForProduction;
    this._dateCreated = dateCreated;
  }

  public get id(): number {
    return this._id;
  }

  public set id(value: number) {
    this._id = value;
  }

  public get name(): string {
    return this._name;
  }

  public set name(value: string) {
    this._name = value;
  }

  public get status(): ProductionScheduleStatus {
    return this._status;
  }

  public set status(value: ProductionScheduleStatus) {
    this._status = value;
  }

  public get state(): ProductionScheduleState {
    return this._state;
  }

  public set state(value: ProductionScheduleState) {
    this._state = value;
  }

  public get dueDate(): Date {
    return this._dueDate;
  }

  public set dueDate(value: Date) {
    this._dueDate = value;
  }

  public get plasticProduct(): IdNameUnitOfMeasurement {
    return this._plasticProduct;
  }

  public set plasticProduct(value: IdNameUnitOfMeasurement) {
    this._plasticProduct = value;
  }

  public get plannedQuantity(): number {
    return this._plannedQuantity;
  }

  public set plannedQuantity(value: number) {
    this._plannedQuantity = value;
  }

  public get machine(): IdName {
    return this._machine;
  }

  public set machine(value: IdName) {
    this._machine = value;
  }

  public get machineGroup(): IdName {
    return this._machineGroup;
  }

  public set machineGroup(value: IdName) {
    this._machineGroup = value;
  }

  public get tool(): IdName {
    return this._tool;
  }

  public set tool(value: IdName) {
    this._tool = value;
  }

  public get customer(): IdName {
    return this._customer;
  }

  public set customer(value: IdName) {
    this._customer = value;
  }

  public get plannedSpeed(): number {
    return this._plannedSpeed;
  }

  public set plannedSpeed(value: number) {
    this._plannedSpeed = value;
  }

  public get plannedEfficiency(): number {
    return this._plannedEfficiency;
  }

  public set plannedEfficiency(value: number) {
    this._plannedEfficiency = value;
  }

  public get releaseForProduction(): boolean {
    return this._releaseForProduction;
  }

  public set releaseForProduction(value: boolean) {
    this._releaseForProduction = value;
  }

  public get dateCreated(): Date {
    return this._dateCreated;
  }

  public set dateCreated(value: Date) {
    this._dateCreated = value;
  }

  public static fromJSON(plasticProductionOrderJSON: any): PlasticProductionOrder {
    return new PlasticProductionOrder(
      plasticProductionOrderJSON.id,
      plasticProductionOrderJSON.name,
      plasticProductionOrderJSON.status,
      plasticProductionOrderJSON.state ? ProductionScheduleState.fromJSON(plasticProductionOrderJSON.state) : null,
      plasticProductionOrderJSON.dueDate ? new Date(plasticProductionOrderJSON.dueDate) : null,
      plasticProductionOrderJSON.plasticProduct ? IdNameUnitOfMeasurement.fromJSON(plasticProductionOrderJSON.plasticProduct) : null,
      plasticProductionOrderJSON.plannedQuantity,
      plasticProductionOrderJSON.machine ? IdName.fromJSON(plasticProductionOrderJSON.machine) : null,
      plasticProductionOrderJSON.machineGroup ? IdName.fromJSON(plasticProductionOrderJSON.machineGroup) : null,
      plasticProductionOrderJSON.tool ? IdName.fromJSON(plasticProductionOrderJSON.tool) : null,
      plasticProductionOrderJSON.customer ? IdName.fromJSON(plasticProductionOrderJSON.customer) : null,
      plasticProductionOrderJSON.plasticProduct.speedUnit === StandardSpeed.SPEED
        ? convertSpeedAndCycleTime(plasticProductionOrderJSON.plannedSpeedInCycleTime)
        : plasticProductionOrderJSON.plannedSpeedInCycleTime,
      plasticProductionOrderJSON.plannedEfficiency,
      plasticProductionOrderJSON.releaseForProduction,
      plasticProductionOrderJSON.dateCreated ? new Date(plasticProductionOrderJSON.dateCreated) : null
    );
  }

  public toJSON(): JSON {
    return {
      name: this.name,
      dueDate: this._dueDate ? moment(this._dueDate).startOf('day').subtract(this._dueDate.getTimezoneOffset(), 'minutes').toISOString() : null,
      plasticProduct: this._plasticProduct ? this._plasticProduct.toJSON() : null,
      plannedQuantity: this._plannedQuantity,
      machineId: this._machine?.id,
      machineName: this._machine?.name,
      machineGroupId: this._machineGroup?.id,
      tool: this._tool ? this._tool.toJSON() : null,
      customerId: this._customer?.id,
      plannedSpeedInCycleTime: this._plasticProduct?.speedUnit === StandardSpeed.SPEED ? convertSpeedAndCycleTime(this._plannedSpeed) : this.plannedSpeed,
      plannedEfficiency: this._plannedEfficiency,
      releaseForProduction: this._releaseForProduction
    } as any as JSON;
  }

  public canEdit(): boolean {
    const EDITABLE_STATUSES = [ProductionScheduleStatus.TO_PLAN, ProductionScheduleStatus.CREATING, ProductionScheduleStatus.RELEASED];

    return EDITABLE_STATUSES.includes(this.status);
  }
}
